/**
 * Canonical role constants + page visibility allow-lists.
 *
 * Single source of truth: update the matrix here, and both the router
 * (App.tsx) and the sidebar (Sidebar.tsx) pick it up.
 *
 * See `docs/rbac-page-matrix.md` for the human-readable matrix.
 * Matrix status: DRAFT — awaiting customer sign-off for Hibernia MVP (#51).
 */

export const ROLES = {
  PLATFORM_ADMIN: 'platform_admin',
  PARENT_ORG_ADMIN: 'parent_org_admin',
  CUSTOMER_ADMIN: 'customer_admin',
  PORTFOLIO_MANAGER: 'portfolio_manager',
  SITE_OPERATOR: 'site_operator',
  ANALYST: 'analyst',
  SITE_VIEWER: 'site_viewer',
} as const;

export type Role = typeof ROLES[keyof typeof ROLES];

export const ADMIN_ROLES: Role[] = [
  ROLES.PLATFORM_ADMIN,
  ROLES.PARENT_ORG_ADMIN,
  ROLES.CUSTOMER_ADMIN,
];

/**
 * Djin-internal-ops-only allow-list. Used for surfaces that are explicitly
 * for Djin support engineers, not customer self-service (e.g. notification
 * delivery audit view — issue #265). customer_admin is intentionally
 * excluded.
 */
export const OPS_ADMIN_ROLES: Role[] = [
  ROLES.PLATFORM_ADMIN,
  ROLES.PARENT_ORG_ADMIN,
];

export const ALL_AUTHENTICATED_ROLES: Role[] = [
  ...ADMIN_ROLES,
  ROLES.PORTFOLIO_MANAGER,
  ROLES.SITE_OPERATOR,
  ROLES.ANALYST,
  ROLES.SITE_VIEWER,
];

// Per-page allow-lists — first draft, awaiting customer red-pen.
export const ROLES_WITH_CHARTS: Role[] = ALL_AUTHENTICATED_ROLES.filter(
  (r) => r !== ROLES.SITE_VIEWER,
);

export const ROLES_WITH_HISTORIAN: Role[] = ALL_AUTHENTICATED_ROLES.filter(
  (r) => r !== ROLES.SITE_VIEWER,
);

export const ROLES_WITH_SECURITY: Role[] = [
  ...ADMIN_ROLES,
  ROLES.PORTFOLIO_MANAGER,
  ROLES.SITE_OPERATOR,
];

// Per Brian's feedback on 2026-05-07 (#270): Local Access lives at the
// edge HMI and is reserved for operators who actually administer the
// site. Analysts and read-only site viewers don't need (and shouldn't
// see) a path that surfaces the local-network URL or the connectivity
// probe — they have no operational use for it.
export const ROLES_WITH_LOCAL_ACCESS: Role[] = [
  ...ADMIN_ROLES,
  ROLES.PORTFOLIO_MANAGER,
  ROLES.SITE_OPERATOR,
];
